import React, { useCallback } from "react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Slider from "react-slick";
import { CatalogOffering, PartnerCatalog } from "../../data/catalog";
import { buildPathWithParams, useMediaQuery } from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { useAnalytics } from "../../lib/analytics/analytics";
import { useGetLightPartnerOffering } from "../../data/api";
import { COMING_FROM_CATALOG } from "../hooks/use_handle_press_back";
import { usePartnerCatalogsQuery } from "@/offering/api";
import classNames from "classnames";

interface ExploreMembershipsProps {
  title: string;
  partnerSlug: string;
  fromCatalog?: string;
}

const getMembershipOfferings = (partnerCatalog?: PartnerCatalog) => {
  if (!partnerCatalog) {
    return null;
  }

  const offerings = partnerCatalog.partnerCatalogCategories.flatMap(
    (category) => category.catalogOfferings ?? []
  );

  return offerings.filter((offering) => !!offering.membership);
};

export function ExploreMemberships(props: ExploreMembershipsProps) {
  const { title, partnerSlug, fromCatalog } = props;
  const { device } = useMediaQuery();
  const { locale } = useI18n();

  const partnerCatalogs = usePartnerCatalogsQuery(partnerSlug);
  const partnerCatalog = fromCatalog
    ? partnerCatalogs.find((pc) => pc.slug === fromCatalog)
    : partnerCatalogs.find((pc) => !pc.slug) || partnerCatalogs[0];

  const offerings = getMembershipOfferings(partnerCatalog);

  if (!offerings?.length) {
    return null;
  }

  if (offerings.length === 1) {
    return (
      <div
        style={{
          backgroundColor:
            "color-mix(in srgb, var(--explore-memberships-block-background) 10%, transparent)",
        }}
        className="container-fluid"
      >
        <div className="py-5">
          <div
            className={classNames("py-4", device !== "mobile" && "container")}
          >
            <h2 className="mb-4 fw-bold">{title}</h2>
            <div className="row">
              <div className="col-12">
                <MembershipCard
                  offering={offerings[0]}
                  catalogSlug={fromCatalog}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--explore-memberships-block-background) 10%, transparent)",
      }}
      className="container-fluid"
    >
      <div className="py-5">
        <div
          className={classNames(
            "explore-memberships",
            device !== "mobile" && "container"
          )}
        >
          <h2 className="mb-4 fw-bold">{title}</h2>
          <div className="row">
            <Slider
              slidesToShow={1}
              arrows={device !== "mobile"}
              dots
              infinite={false}
              rtl={locale.direction === "rtl"}
            >
              {offerings.map((offering) => (
                <div key={offering.id}>
                  <MembershipCard
                    offering={offering}
                    catalogSlug={fromCatalog}
                  />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MembershipCardProps {
  offering: CatalogOffering;
  catalogSlug?: string;
}

function MembershipCard({ offering, catalogSlug }: MembershipCardProps) {
  const history = useHistory();
  const { fbt } = useI18n();
  const analytics = useAnalytics();
  const { device } = useMediaQuery();
  const partnerOffering = useGetLightPartnerOffering(offering.slug);

  const handleButton = useCallback(() => {
    analytics.event(
      `Membership Card Click`,
      {
        Template: partnerOffering.template,
        Title: offering.title,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );

    const pathWithParams = buildPathWithParams(
      catalogSlug
        ? `/offerings/${offering.slug}?fromCatalog=${catalogSlug}`
        : `/offerings/${offering.slug}`,
      {
        reset: true,
        keepOnReset: ["fromCatalog", "locationID", "fromCategory", "promoCode"],
      }
    );

    history.push(pathWithParams, { comingFrom: COMING_FROM_CATALOG });
  }, [analytics, offering, catalogSlug, history, partnerOffering.template]);

  return (
    <div
      className="d-flex flex-column-reverse flex-md-row flex-grow-1 h-100"
      style={{ minHeight: device === "mobile" ? "auto" : "295px" }}
    >
      <div
        className="flex-grow-1 d-flex flex-column justify-content-center gap-3 p-4"
        style={{
          borderRadius: device === "mobile" ? "0 0 8px 8px" : "8px 0 0 8px",
          backgroundColor: "var(--explore-memberships-background)",
          flex: device === "mobile" ? undefined : 1,
        }}
      >
        <h3 className="fw-bold mb-0">{offering.title}</h3>
        <p className="text-muted mb-0">{offering.description}</p>
        <Button
          variant="outline-primary"
          onClick={handleButton}
          className="align-self-start"
          style={{
            padding: "8px 16px",
            fontWeight: 600,
            borderRadius: "4px",
          }}
        >
          {fbt("See details")}
        </Button>
      </div>
      <div
        style={{
          flex: device === "mobile" ? undefined : 2,
          width: device === "mobile" ? "100%" : "60%",
          height: device === "mobile" ? "160px" : "auto",
          backgroundImage: `url(${offering.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: device === "mobile" ? "8px 8px 0 0" : "0 8px 8px 0",
        }}
      />
    </div>
  );
}
